@if (showBoostInfoView() && activePurchaseBoost(); as boost) {
  @if (boost.isFirstTimePurchaseBoost) {
    <app-first-time-boost-view
      [boost]="boost"
      (back)="showBoostInfoView.set(false)"
      (selectAmount)="handleBoostTierSelect($event)"
      (close)="close.emit()"
    />
  } @else {
    <app-regular-boost-view
      [boost]="boost"
      (back)="showBoostInfoView.set(false)"
      (close)="close.emit()"
    />
  }
} @else {
  <ac-card>
    <ac-card-content>
      <div class="purchase-dialog">
        <app-purchase-dialog-header
          title="Buy Coins"
          [hideBack]="true"
          (close)="close.emit()"
        />

        @if (isLoading()) {
          <div
            class="buy-coins__loading ac-ctr ac-flex"
            [style.margin]="'auto'">
            <ac-loading type="spinner" [size]="32" />
          </div>
        } @else {
          <!-- Amount Input Area -->
          <div class="buy-coins__amount-area" [class.buy-coins__amount-area--faded]="fiatRowsDisabled()">
            <div class="buy-coins__amount-display">
              <span class="buy-coins__dollar-sign">$</span>
              <input
                #customAmountInput
                class="buy-coins__amount-input"
                [class.buy-coins__amount-input--empty]="!customAmount().gt(0)"
                type="text"
                inputmode="decimal"
                placeholder="0"
                [formControl]="amount"
                (keydown)="amountKeydown($event)"
                autocomplete="off"
              />
            </div>

            <!-- Coin estimates (shown when custom amount is above zero) -->
            <div class="buy-coins__coin-estimate">
              <app-gc-icon [size]="16" />
              <span>{{ customAmountSelection().gcAwarded.toNumber() | number }}</span>
              <span class="ac-flex ac-algn-ctr ac-gap-xs" sweepsVisible>
                <span class="buy-coins__coin-estimate-plus">+</span>
                <app-sc-icon [size]="16" />
                <span>{{ customAmountSelection().scAwarded.toNumber() | decimal : '2-4' }}</span>
                <span class="buy-coins__coin-estimate-free">Free</span>
              </span>
            </div>
          </div>

          <div>
            <!-- FTP Boost Banner (below coin estimate, above pills) -->
            @if (activePurchaseBoost()?.isFirstTimePurchaseBoost) {
              <div class="ac-flex ac-ctr">
                <app-purchase-boost-banner (openDetails)="showBoostInfoView.set(true)" />
              </div>
            }

            <!-- Regular Boost Banner (non-FTP, green pill after pills) -->
            @if (activePurchaseBoost() && !activePurchaseBoost()!.isFirstTimePurchaseBoost) {
              <div class="ac-flex ac-ctr">
                <app-purchase-boost-banner (openDetails)="showBoostInfoView.set(true)" />
              </div>
            } @else if (showPromoBanner()) {
              @if (promosExpanded()) {
                <!-- Expanded Promotions Banner (yellow with close) -->
                <div class="ac-flex ac-ctr">
                  <div class="buy-coins__promo-banner" (click)="togglePromosExpanded()">
                    <ac-icon iconKey="hr:gift" [size]="16" />
                    <span class="buy-coins__promo-banner-text">
                    {{ availablePromoCount() }} {{ availablePromoCount() === 1 ? 'PROMO' : 'PROMOS' }} AVAILABLE
                  </span>
                    <ac-icon iconKey="hr:xmark_circle" [size]="16" />
                  </div>
                </div>
              } @else {
                <!-- Collapsed Promotions Banner (yellow pill with arrow) -->
                <div class="ac-flex ac-ctr">
                  <div class="buy-coins__promo-banner" (click)="togglePromosExpanded()">
                    <ac-icon iconKey="hr:gift" [size]="16" />
                    <span class="buy-coins__promo-banner-text">
                    {{ availablePromoCount() }} {{ availablePromoCount() === 1 ? 'PROMO' : 'PROMOS' }} AVAILABLE
                  </span>
                    <ac-icon iconKey="hr:chevron_right" [size]="16" />
                  </div>
                </div>
              }
            }

            <!-- Single slot: errors > warning > default boost text -->
            @if (purchaseCapReached()) {
              <div class="ac-body-md buy-coins__validation buy-coins__validation--error">
                {{ purchaseCapMessage }}
              </div>
            } @else if (customAmount().gt(maxAmountUsd)) {
              <div class="ac-body-md buy-coins__validation buy-coins__validation--error">
                Maximum purchase is {{ maxAmountUsd | currency : 'USD' : 'symbol-narrow' : '1.0-0' }}
              </div>
            } @else if (showPaymentMethodError()) {
              <div class="ac-body-md buy-coins__validation buy-coins__validation--error">
                Please enter a valid amount between $10 and {{ maxAmountUsd | currency : 'USD' : 'symbol-narrow' : '1.0-0' }}
              </div>
            } @else if (amount.hasError('min') && amount.touched) {
              <div class="ac-body-md buy-coins__validation buy-coins__validation--error">
                Minimum purchase is $10
              </div>
            } @else if (boostCapExceededWarning()) {
              <div class="ac-body-md buy-coins__validation buy-coins__validation--warning">
                {{ boostCapExceededWarning() }}
              </div>
            } @else if (boostMaxPurchaseText()) {
              <div class="ac-body-md buy-coins__validation buy-coins__max-purchase-text">
                {{ boostMaxPurchaseText() }}
              </div>
            }
          </div>

          <!-- Quick Amount Pills -->
          @if (ftpPills(); as pills) {
            <div class="buy-coins__ftp-grid" [class.buy-coins__ftp-grid--faded]="fiatRowsDisabled()">
              @for (pill of pills; track pill.amount) {
                <button
                  class="buy-coins__ftp-pill"
                  [style.border-color]="customAmount().eq(pill.amount) ? pill.color : 'transparent'"
                  type="button"
                  (click)="selectPreset(pill.amount)">
                  <span class="buy-coins__ftp-pill-amount">
                    {{ pill.amount | currency : 'USD' : 'symbol-narrow' : '1.0-0' }}
                  </span>
                  @if (pill.label) {
                    <span class="buy-coins__ftp-pill-tag">
                      <span [style.color]="pill.color">{{ pill.label }}</span>
                      @if (pill.bonusPercent) {
                        <span class="buy-coins__ftp-pill-sep"> &bull; </span>
                        <span class="buy-coins__ftp-pill-bonus">{{ pill.bonusPercent }}% BONUS</span>
                      }
                    </span>
                  }
                </button>
              }
            </div>
          } @else {
            <div class="buy-coins__pills" [class.buy-coins__pills--faded]="fiatRowsDisabled()">
              @for (preset of presetAmounts(); track preset) {
                <button
                  class="buy-coins__pill"
                  [class.buy-coins__pill--active]="customAmount().eq(preset)"
                  type="button"
                  (click)="selectPreset(preset)">
                  {{ preset | currency : 'USD' : 'symbol-narrow' : '1.0-0' }}
                </button>
              }
            </div>
          }

          <!-- Promotions List (expanded state) -->
          @if (promosExpanded() && !activePurchaseBoost()) {
            <div class="buy-coins__promos-list">
              <div class="buy-coins__promos-list-label">Available Promotions</div>
              @for (promo of availablePromotions(); track promo.user_promotion_id) {
                <div class="buy-coins__promo-card">
                  <div class="buy-coins__promo-card-icon">
                    <ac-icon iconKey="payment:lightning" [size]="24" />
                  </div>
                  <div class="buy-coins__promo-card-content">
                    <div class="buy-coins__promo-card-title">{{ promo.title }}</div>
                    <div class="buy-coins__promo-card-subtitle">
                      @if (getBoostTierRange(promo); as tierRange) {
                        <span sweepsVisible>Up to {{ tierRange }} SC bonus on your first purchase</span>
                      } @else if (getBoostMaxAmount(promo)) {
                        On any purchase up to {{ getBoostMaxAmount(promo) }}
                      } @else {
                        {{ promo.description }}
                      }
                    </div>
                  </div>
                  <button
                    class="buy-coins__promo-activate-btn"
                    type="button"
                    [disabled]="promotionBeingClaimed() !== null"
                    (click)="claimPromotion(promo.user_promotion_id)">
                    @if (promotionBeingClaimed() === promo.user_promotion_id) {
                      <ac-loading type="spinner" [size]="16" />
                    } @else {
                      Activate
                    }
                  </button>
                </div>
              }
            </div>
          }

          <!-- Payment Methods (hidden when promos expanded) -->
          @if (!promosExpanded()) {
            <div class="buy-coins__payment-methods">
              <!-- Card Payment Group -->
              <div class="buy-coins__card-group" [class.buy-coins__card-group--disabled]="fiatRowsDisabled() || purchaseCapReached()">
                <!-- New Card -->
                <button
                  class="buy-coins__card-group-row"
                  type="button"
                  [disabled]="fiatRowsDisabled() || purchaseCapReached()"
                  (click)="selectPaymentMethod('card')">
                  <div class="buy-coins__payment-method-icon">
                    <ac-icon iconKey="payment:debit_credit_card" [size]="24" />
                  </div>
                  <div class="buy-coins__payment-method-info">
                    <div class="buy-coins__payment-method-title">Card</div>
                    <div class="buy-coins__payment-method-subtitle">Pay with debit or credit card</div>
                  </div>
                  @if (fiatRowsDisabled()) {
                    <span class="buy-coins__unavailable-chip">Unavailable</span>
                  } @else {
                    <div class="buy-coins__payment-method-badges">
                      <ac-icon iconKey="payment:visa" [size]="20" />
                      <ac-icon iconKey="payment:mastercard" [size]="20" />
                    </div>
                    <ac-icon iconKey="hr:chevron_right" [size]="14" />
                  }
                </button>

                <!-- Saved Cards (hidden when fiat disabled) -->
                @if (!fiatRowsDisabled()) {
                  @if (savedCardsSyncing()) {
                    <div class="buy-coins__card-group-row ac-ctr ac-flex" [style.justify-content]="'center'">
                      <ac-loading type="spinner" [size]="24" />
                    </div>
                  } @else {
                  @for (card of savedCards(); track card.payment_method_id) {
                    <button
                      class="buy-coins__card-group-row"
                      type="button"
                      [disabled]="savedCardsSyncing() || purchaseCapReached()"
                      (click)="selectSavedCard(card)">
                      <div class="buy-coins__payment-method-icon">
                      @switch (card.metadata?.cardType) {
                        @case ('VISA') {
                          <ac-icon iconKey="payment:visa" [size]="24" />
                        }
                        @case ('MSTR') {
                          <ac-icon iconKey="payment:mastercard" [size]="24" />
                        }
                        @case ('AMEX') {
                          <ac-icon iconKey="payment:amex" [size]="24" />
                        }
                        @case ('DISC') {
                          <ac-icon iconKey="payment:discover" [size]="24" />
                        }
                        @default {
                          <ac-icon iconKey="payment:debit_credit_card" [size]="24" />
                        }
                      }
                    </div>
                      <div class="buy-coins__payment-method-info">
                        <div class="buy-coins__payment-method-title">{{ card.label }}</div>
                        <div class="buy-coins__payment-method-subtitle">Saved</div>
                      </div>
                      <ac-icon iconKey="hr:chevron_right" [size]="14" />
                    </button>
                  }
                  }
                }
              </div>

              <!-- Apple Pay -->
              <button
                class="buy-coins__payment-method-row"
                [class.buy-coins__payment-method-row--disabled]="fiatRowsDisabled() || purchaseCapReached()"
                type="button"
                [disabled]="fiatRowsDisabled() || purchaseCapReached()"
                (click)="selectPaymentMethod('apple-pay')">
                <div class="buy-coins__payment-method-icon">
                  <ac-icon iconKey="social:apple_2" [size]="28" />
                </div>
                <div class="buy-coins__payment-method-info">
                  <div class="buy-coins__payment-method-title">Apple Pay</div>
                  <div class="buy-coins__payment-method-subtitle">Pay with Apple Pay</div>
                </div>
                @if (fiatRowsDisabled()) {
                  <span class="buy-coins__unavailable-chip">Unavailable</span>
                } @else {
                  <ac-icon iconKey="hr:chevron_right" [size]="14" />
                }
              </button>

              <!-- Google Pay -->
              <button
                class="buy-coins__payment-method-row"
                [class.buy-coins__payment-method-row--disabled]="fiatRowsDisabled() || purchaseCapReached()"
                type="button"
                [disabled]="fiatRowsDisabled() || purchaseCapReached()"
                (click)="selectPaymentMethod('google-pay')">
                <div class="buy-coins__payment-method-icon">
                  <ac-icon iconKey="social:google_color" [size]="28" />
                </div>
                <div class="buy-coins__payment-method-info">
                  <div class="buy-coins__payment-method-title">Google Pay</div>
                  <div class="buy-coins__payment-method-subtitle">Pay with Google Pay</div>
                </div>
                @if (fiatRowsDisabled()) {
                  <span class="buy-coins__unavailable-chip">Unavailable</span>
                } @else {
                  <ac-icon iconKey="hr:chevron_right" [size]="14" />
                }
              </button>

              <!-- Crypto Payment -->
              @if (cryptoPurchaseEnabled()) {
                <button
                  class="buy-coins__payment-method-row"
                  [class.buy-coins__payment-method-row--disabled]="purchaseCapReached()"
                  type="button"
                  [disabled]="purchaseCapReached()"
                  (click)="selectPaymentMethod('crypto')">
                  <div class="buy-coins__payment-method-icon">
                    <ac-icon iconKey="payment:bitcoin_b" [size]="24" />
                  </div>
                  <div class="buy-coins__payment-method-info">
                    <div class="buy-coins__payment-method-title">Crypto</div>
                    <div class="buy-coins__payment-method-subtitle">Pay with Crypto</div>
                  </div>
                  <div class="buy-coins__payment-method-badges">
                    <ac-icon iconKey="currency:bitcoin" [size]="18" />
                    <ac-icon iconKey="currency:ethereum" [size]="18" />
                    <ac-icon iconKey="currency:solana" [size]="18" />
                    <ac-icon iconKey="currency:usdc" [size]="18" />
                    <ac-icon iconKey="currency:usdt" [size]="18" />
                  </div>
                  <ac-icon iconKey="hr:chevron_right" [size]="14" />
                </button>
              }
            </div>
          }
        }
      </div>
    </ac-card-content>
  </ac-card>
}
